import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Seo } from "../components/Seo";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { CtaBand } from "../components/home/CtaBand";
import { Eyebrow, Reveal, slug } from "../components/motion";
import { CASE_STUDIES } from "../data/content";

const Rows = ({ rows, columns, testId }) => (
  <dl className="divide-y divide-white/10 border-y border-white/10" data-testid={testId}>
    {columns && (
      <div className="hidden py-3 sm:grid sm:grid-cols-[1fr_8rem_8rem] sm:gap-4">
        <span />
        {columns.map((c) => (
          <span key={c} className="text-right font-mono text-[9px] uppercase tracking-[0.15em] text-white/45">{c}</span>
        ))}
      </div>
    )}
    {rows.map((r) => (
      <div
        key={r.label}
        className={`py-4 ${r.values ? "grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-[1fr_8rem_8rem] sm:items-baseline" : "flex items-baseline justify-between gap-6"} ${r.strong ? "-mx-4 bg-neon/[0.06] px-4" : ""}`}
      >
        <dt className={`text-sm ${r.values ? "col-span-2 sm:col-span-1" : ""} ${r.strong ? "text-cream" : "text-white/55"}`}>{r.label}</dt>
        {r.values ? (
          r.values.map((v, i) => (
            <dd key={i} className="sm:text-right">
              <span className="block font-mono text-[8px] uppercase tracking-[0.15em] text-white/35 sm:hidden">{columns?.[i]}</span>
              <span className={`font-serif ${r.strong ? "text-2xl text-neon-light sm:text-3xl" : "text-lg text-cream sm:text-xl"}`}>{v}</span>
            </dd>
          ))
        ) : (
          <dd className={`shrink-0 text-right font-serif ${r.strong ? "text-3xl text-neon-light" : "text-xl text-cream"}`}>{r.value}</dd>
        )}
      </div>
    ))}
  </dl>
);

const SectionHead = ({ eyebrow, title }) => (
  <>
    <Reveal>
      <Eyebrow>{eyebrow}</Eyebrow>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">{title}</h2>
    </Reveal>
  </>
);

export default function EtudeDeCas() {
  const navigate = useNavigate();
  const { slug: key } = useParams();
  const CS = CASE_STUDIES[key];
  if (!CS) return <Navigate to="/realisations" replace />;

  return (
    <main data-testid="page-etude-de-cas" key={key}>
      <Seo title={CS.seoTitle} description={CS.seoDescription} />

      <section className="px-6 pb-16 pt-40 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1500px]">
          <Link to="/realisations" data-testid="case-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 transition-colors hover:text-neon">
            <ArrowLeft className="h-3.5 w-3.5" /> Toutes les réalisations
          </Link>
          <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <Eyebrow>{CS.eyebrow}</Eyebrow>
              <h1 className="mt-6 font-serif text-5xl leading-[1.06] text-cream sm:text-6xl lg:text-7xl" data-testid="case-title">
                {CS.title}
              </h1>
            </div>
            <p className="leading-relaxed text-white/60 lg:col-span-4">{CS.intro}</p>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4" data-testid="case-kpis">
            {CS.kpis.map((k) => (
              <div key={k.label} className="bg-ink p-6 lg:p-8">
                <p className="whitespace-nowrap font-serif text-3xl text-neon-light sm:text-5xl">{k.value}</p>
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <div className="relative aspect-[16/9] overflow-hidden lg:aspect-[21/9]">
              <img src={CS.cover} alt={CS.coverAlt} className="h-full w-full object-cover" data-testid="case-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="case-profil-projet">
        <div className="mx-auto grid max-w-[1500px] gap-16 px-6 sm:px-10 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Le profil" title={CS.profileTitle} />
            <Reveal delay={0.12}>
              <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-2" data-testid="case-profile">
                {CS.profile.map((p) => (
                  <div key={p.label} className="border-l border-neon/40 pl-4">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">{p.label}</p>
                    <p className="mt-1 font-serif text-2xl text-cream">{p.value}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHead eyebrow="Le projet" title={CS.projectTitle} />
            <Reveal delay={0.12} className="mt-10">
              <Rows rows={CS.project} testId="case-project-rows" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#1D1327] py-24 lg:py-32" data-testid="case-transformation">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <SectionHead eyebrow="La transformation" title={CS.steps.length === 3 ? "Avant. Pendant. Après." : "Avant. Après."} />
          <Reveal delay={0.1}>
            <div className="mx-auto mt-12 max-w-5xl">
              <BeforeAfterSlider before={CS.beforeAfter.avant} after={CS.beforeAfter.apres} altBefore={CS.beforeAfter.altAvant} altAfter={CS.beforeAfter.altApres} />
            </div>
          </Reveal>

          <div className="mt-20 space-y-20">
            {CS.steps.map((s, i) => (
              <div key={s.tag} className="grid gap-10 lg:grid-cols-12" data-testid={`case-step-${slug(s.tag)}`}>
                <div className={`lg:col-span-4 ${i % 2 ? "lg:order-2 lg:col-start-9" : ""}`}>
                  <Reveal>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon">
                      {String(i + 1).padStart(2, "0")} — {s.tag}
                    </p>
                    <h3 className="mt-4 font-serif text-3xl leading-tight text-cream">{s.title}</h3>
                    <p className="mt-5 leading-relaxed text-white/55">{s.text}</p>
                  </Reveal>
                </div>
                <div className={`grid gap-4 sm:grid-cols-2 lg:col-span-7 ${i % 2 ? "lg:order-1" : "lg:col-start-6"}`}>
                  {s.images.map((src, k) => {
                    const wide = (s.images.length === 3 && k === 0) || s.images.length === 1;
                    return (
                      <Reveal key={src} delay={k * 0.06} className={wide ? "sm:col-span-2" : ""}>
                        <img src={src} alt={`${s.tag} travaux — ${CS.title}, vue ${k + 1}`} loading="lazy" className={`w-full object-cover ${wide ? "aspect-[16/9]" : "aspect-[4/3]"}`} />
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="case-performance">
        <div className="mx-auto grid max-w-[1500px] gap-16 px-6 sm:px-10 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Revenus & résultats" title={CS.performanceTitle} />
            <Reveal delay={0.12}>
              <p className="mt-8 leading-relaxed text-white/55">{CS.performanceNote}</p>
            </Reveal>
            {CS.testimonial && (
              <Reveal delay={0.18}>
                <blockquote className="mt-10 border-l-2 border-neon pl-6" data-testid="case-testimonial">
                  <Quote className="h-5 w-5 text-neon/60" />
                  <p className="mt-4 font-serif text-2xl italic leading-snug text-white/75">« {CS.testimonial.texte} »</p>
                  <footer className="mt-5 flex items-center gap-4">
                    <img src={CS.testimonial.photo} alt={CS.testimonial.auteur} className="h-12 w-12 rounded-full object-cover" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon">{CS.testimonial.auteur}</span>
                  </footer>
                </blockquote>
              </Reveal>
            )}
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-[#1D1327] p-6 sm:p-8 lg:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">{CS.performanceLabel}</p>
                <div className="mt-6">
                  <Rows rows={CS.performance} columns={CS.performanceColumns} testId="case-performance-rows" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 text-xs leading-relaxed text-white/35">{CS.disclaimer}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {CS.reviews.length > 0 && (
        <section className="border-t border-white/10 px-6 py-20 sm:px-10 lg:px-16" data-testid="case-reviews">
          <div className="mx-auto max-w-[1500px]">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">Captures réelles de l'annonce & des avis voyageurs</p>
            </Reveal>
            <div className="mt-6 grid items-start gap-6 md:grid-cols-3">
              {CS.reviews.map((r, i) => (
                <Reveal key={r.image} delay={i * 0.06}>
                  <img src={r.image} alt={r.alt} loading="lazy" className="w-full border border-white/10 bg-white object-contain" />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-white/10 px-6 py-20 sm:px-10 lg:px-16">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <p className="max-w-2xl font-serif text-3xl leading-snug text-cream sm:text-4xl">
            {CS.closing.text} <em className="italic text-neon-light">{CS.closing.em}</em>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={CS.other.to} data-testid="case-other" className="btn-outline">
              {CS.other.label} <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={() => navigate("/rendez-vous")} data-testid="case-cta" className="btn-primary">
              Parler de mon projet <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
